import { z } from 'zod';
import { pageSchema, useApiMutation, useApiQuery, queryKeys, toListQuery, type ListParams } from '@zagru/platform';
import { defineListOnlyResource, defineListResource, usePost, type QueryOpts } from './common';

// ---------- Deployment descriptor ----------
/**
 * `PlatformInfo` (`one.zagru.platform.web.api.PlatformInfo`): what the deployment is, which build is running and
 * where it expects the client to authenticate. `buildCommit`, `buildTime` and `oidc.audience` are serialized with
 * `NON_NULL`, so they are absent rather than null when the deployment does not know them.
 */
export const platformInfoSchema = z.object({
  product: z.string(),
  version: z.string(),
  apiVersion: z.string(),
  environment: z.string(),
  buildCommit: z.string().nullish(),
  buildTime: z.string().nullish(),
  oidc: z.object({
    issuer: z.string(),
    clientId: z.string(),
    scopes: z.array(z.string()).default([]),
    audience: z.string().nullish(),
  }),
  /** Deployment-wide switches only; tenant flags come from `/platform/configuration/effective` after login. */
  features: z.record(z.string(), z.boolean()).default({}),
  serverTime: z.string(),
});
export type PlatformInfo = z.infer<typeof platformInfoSchema>;

/** A version the server could not tell (no build info) reads as absent, never as "unknown". */
export function isKnownVersion(version: string | null | undefined): version is string {
  return !!version && version.trim() !== '' && version.trim().toLowerCase() !== 'unknown';
}

/** F-08: when the server does not know its build, the shell shows the web build's version and commit instead. */
export function withClientBuild(info: PlatformInfo): PlatformInfo {
  if (isKnownVersion(info.version)) return info;
  const version = import.meta.env.VITE_APP_VERSION;
  const commit = import.meta.env.VITE_APP_COMMIT;
  return { ...info, version: isKnownVersion(version) ? version : info.version, buildCommit: info.buildCommit ?? (commit || undefined) };
}

/**
 * Reads the deployment descriptor. This is the one call the app makes **before** it has a token: the backend's
 * `PlatformInfoController` is permitted in the security filter chain for GET, and the API client only sends
 * `Authorization` when a token happens to exist, so no session is required.
 *
 * A failure resolves to `null` instead of rejecting. The only caller is the login screen, which must render and
 * sign a user in whether or not the descriptor answers; letting the query fail would put a problem toast in front
 * of someone who cannot do anything about it.
 */
export function usePlatformInfo(options?: QueryOpts<PlatformInfo | null>) {
  return useApiQuery<PlatformInfo | null>(
    queryKeys.custom('platform', 'info'),
    async (client, signal) => {
      try {
        return withClientBuild(await client.get<PlatformInfo>('/api/v1/platform/info', { schema: platformInfoSchema, signal }));
      } catch {
        return null;
      }
    },
    { staleTime: 5 * 60_000, retry: false, ...(options ?? {}) },
  );
}

// ---------- Configuration ----------
/** `ScopeLevel` (`one.zagru.platform.metadata.api.ScopeLevel`), most general first. A value resolves down this chain. */
export const scopeLevels = ['PLATFORM', 'TENANT', 'PARTY', 'WAREHOUSE', 'PROCESS', 'ROLE', 'USER'] as const;
export type ScopeLevel = (typeof scopeLevels)[number];
export const scopeLevelSchema = z.enum(scopeLevels);

/**
 * `ScopeRef`: a scope instance. PLATFORM and TENANT carry no `scopeId`; the other levels always do. `composite` is
 * true for the party-per-warehouse PROCESS scope (`party:<uuid>/warehouse:<uuid>`).
 */
export const scopeRefSchema = z.object({
  level: scopeLevelSchema,
  scopeId: z.string().nullable(),
  composite: z.boolean(),
});
export type ScopeRef = z.infer<typeof scopeRefSchema>;

/** `ConfigurationDefinition.ValueType`. */
export const configurationValueTypes = ['STRING', 'INTEGER', 'DECIMAL', 'BOOLEAN', 'DURATION', 'ENUM', 'JSON'] as const;
export type ConfigurationValueType = (typeof configurationValueTypes)[number];
/** `ConfigurationDefinition.Sensitivity`: SECRET values are masked by every read. */
export const configurationSensitivities = ['PUBLIC', 'INTERNAL', 'SECRET'] as const;
/** `ConfigurationDefinition.OverridePolicy.Kind`. */
export const overridePolicyKinds = ['OVERRIDABLE', 'LOCKED_ABOVE', 'MERGE'] as const;

/**
 * `ConfigurationDefinition`: a key declared in code by a module. `featureFlag` and `secret` are derived by the
 * backend (`feature.` prefix, SECRET sensitivity) and always present; `defaultValue` is any JSON value, `null` when
 * the definition has no default.
 */
export const configurationDefinitionSchema = z.object({
  key: z.string(),
  type: z.string().optional(),
  valueType: z.enum(configurationValueTypes).optional(),
  jsonSchema: z.string().nullable().optional(),
  enumValues: z.array(z.string()).default([]),
  allowedValues: z.array(z.string()).optional(),
  defaultValue: z.unknown(),
  allowedScopes: z.array(scopeLevelSchema).default([]),
  scopeLevels: z.array(scopeLevelSchema).optional(),
  overridePolicy: z.object({ kind: z.enum(overridePolicyKinds), lockedAbove: scopeLevelSchema.nullable() }).optional(),
  sensitivity: z.enum(configurationSensitivities).optional(),
  description: z.string(),
  owningModule: z.string().optional(),
  module: z.string().optional(),
  requiresRestart: z.boolean().optional(),
  featureFlag: z.boolean().optional(),
  secret: z.boolean().optional(),
  sensitive: z.boolean().optional(),
});
export type ConfigurationDefinition = z.infer<typeof configurationDefinitionSchema>;

/**
 * `ConfigurationResolver.ValueProvenance`: one scope instance on the inheritance chain that supplies the key, most
 * specific first and ending with the definition default. `winner` marks the one that produced the effective value.
 */
export const provenanceEntrySchema = z.object({
  scope: z.string(),
  scopeLevel: scopeLevelSchema.optional(),
  scopeId: z.string().nullable().optional(),
  value: z.unknown(),
  setArtifactId: z.string().nullable().optional(),
  version: z.number().nullable().optional(),
  isDefault: z.boolean().optional(),
  winner: z.boolean().optional(),
});
export type ProvenanceEntry = z.infer<typeof provenanceEntrySchema>;

/**
 * `EffectiveValue`: the resolved value of one key with its provenance. The record is serialized `NON_NULL`, so
 * `scopeId`, `setArtifactId` and `version` are absent (not null) when the value has none. `value` is the plain JSON
 * value, already masked for SECRET keys. The definition (description, allowed scopes, …) is not included; read it
 * from `/configuration/definitions`.
 */
export const effectiveValueSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  valueType: z.enum(configurationValueTypes).optional(),
  scope: z.string(),
  scopeLevel: scopeLevelSchema.optional(),
  scopeId: z.string().optional(),
  setArtifactId: z.string().optional(),
  version: z.number().optional(),
  isDefault: z.boolean().optional(),
  sensitivity: z.enum(configurationSensitivities).optional(),
  definition: configurationDefinitionSchema.optional(),
  provenance: z.array(provenanceEntrySchema),
});
export type EffectiveValue = z.infer<typeof effectiveValueSchema>;

/** `ConfigurationSets.Entry`: one key/value pair inside a configuration set, with an optional validity window. */
export const configurationEntrySchema = z.object({
  key: z.string(),
  value: z.unknown(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
});
export type ConfigurationEntry = z.infer<typeof configurationEntrySchema>;

// ---------- Metadata artifacts ----------
/**
 * `ArtifactKind`, in the order the backend declares it — all of it.
 *
 * This list stopped at 25 while the platform went to 34: the universal logistics and binding waves added
 * CHARACTERISTIC through DOCUMENT_BUNDLE and nobody widened the enum here. The cost was not a missing card.
 * `GET /studio/kinds` answers with every kind, so the array failed `z.enum` on every single load and Studio
 * fell back — silently, by design, so that the designers stay reachable — to the SPA's own kind list with no
 * descriptions and no counts. Worse, an artifact of one of those kinds failed the artifact list parse outright,
 * which is a screen that goes blank rather than a card that is absent.
 *
 * `artifactKinds.test.ts` reads the Java enum and fails on the next drift, so this cannot happen a third time.
 */
export const artifactKinds = [
  'WORKFLOW', 'RULESET', 'SCREEN', 'FORM', 'MOBILE_SCREEN', 'DASHBOARD', 'REPORT', 'DOCUMENT', 'LABEL', 'NOTIFICATION',
  'KPI', 'INTEGRATION_MAPPING', 'PERMISSION_SET', 'AGENT', 'TASK_TYPE', 'CONFIGURATION_SET', 'SEMANTIC_MODEL',
  'ENTITY_EXTENSION', 'LOCATION_TYPE', 'INVENTORY_STATUS', 'PARTY_ROLE_TYPE', 'CHARGE_TYPE', 'MILESTONE_TYPE',
  'EXCEPTION_TYPE', 'CHARACTERISTIC', 'TRANSPORT_MODE', 'RESOURCE_TYPE', 'EQUIPMENT_TYPE', 'SHIPMENT_PROFILE',
  'PROCESS_EXPERIENCE', 'INTEGRATION_BINDING', 'DOCUMENT_BINDING', 'DOCUMENT_BUNDLE', 'COMPLIANCE_DOCUMENT_TYPE', 'PACK',
] as const;
export type ArtifactKind = (typeof artifactKinds)[number];
export type ArtifactKindRecord<T> = Partial<Record<ArtifactKind, T>>;
export const artifactKindSchema = z.enum(artifactKinds);

/** `ArtifactStatus`: the lifecycle of one version (ADR-0009). */
export const artifactStatuses = ['DRAFT', 'SANDBOX', 'TEST', 'APPROVAL', 'PUBLISHED', 'SUPERSEDED', 'ROLLED_BACK', 'REJECTED'] as const;
export type ArtifactStatus = (typeof artifactStatuses)[number];
export const artifactStatusSchema = z.enum(artifactStatuses);
/** Statuses of the one version per artifact that is being worked on towards publication. */
export const inFlightArtifactStatuses = ['DRAFT', 'SANDBOX', 'TEST', 'APPROVAL'] as const satisfies readonly ArtifactStatus[];

/** `SourceType`: where the content of a version came from. */
export const artifactSourceTypes = ['STUDIO', 'AI_PROPOSAL', 'PACK', 'API', 'MIGRATION'] as const;

/** `ArtifactApi.ArtifactVersionView`. `version` is the version number the version routes take. */
export const artifactVersionSchema = z.object({
  id: z.string().optional(),
  artifactId: z.string().optional(),
  version: z.number(),
  status: artifactStatusSchema,
  content: z.unknown(),
  schemaVersion: z.string(),
  dependencies: z.array(z.object({ kind: artifactKindSchema, key: z.string(), versionConstraint: z.string().nullable().optional() })),
  effectiveFrom: z.string().nullable(),
  changeNote: z.string().nullable(),
  sourceType: z.enum(artifactSourceTypes),
  checksum: z.string(),
  restoredFromVersion: z.number().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type ArtifactVersion = z.infer<typeof artifactVersionSchema>;

/**
 * `ArtifactApi.ArtifactView`. An artifact has at most one live version (`currentPublishedVersion`) and at most one
 * version in flight (`inFlightVersion` / `inFlightStatus`); use {@link artifactLatestVersion} and
 * {@link artifactLatestStatus} for "the version you would open". `tenantId` is null for platform-level artifacts.
 */
export const artifactSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable().optional(),
  kind: artifactKindSchema,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  tags: z.array(z.string()).default([]),
  scope: z.union([scopeRefSchema, z.string()]),
  scopeId: z.string().nullable().optional(),
  ownerPackId: z.string().nullable(),
  basedOnPackVersion: z.string().nullable().optional(),
  currentPublishedVersion: z.number().nullable(),
  inFlightVersion: z.number().nullable().optional(),
  inFlightStatus: artifactStatusSchema.nullable().optional(),
  validated: z.boolean().optional(),
  rowVersion: z.number().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable(),
  /** Who last changed it. Null before auditing existed, and for platform-level seeds nobody here edited. */
  updatedBy: z.string().nullable().optional(),
}).transform((artifact) => ({
  ...artifact,
  tenantId: artifact.tenantId ?? null,
  scope: typeof artifact.scope === 'string' ? { level: artifact.scope as ScopeLevel, scopeId: artifact.scopeId ?? null, composite: false } : artifact.scope,
  basedOnPackVersion: artifact.basedOnPackVersion ?? null,
  inFlightVersion: artifact.inFlightVersion ?? null,
  inFlightStatus: artifact.inFlightStatus ?? null,
  validated: artifact.validated ?? false,
  rowVersion: artifact.rowVersion ?? 0,
  createdAt: artifact.createdAt ?? artifact.updatedAt,
  updatedBy: artifact.updatedBy ?? null,
}));
type ArtifactBase = z.infer<typeof artifactSchema>;
/**
 * `ArtifactView`. `latestVersion` / `latestStatus` were once declared here and are **not** on the wire —
 * the schema dropped them, so `artifactLatestVersion` silently fell back to the published version and the
 * Studio grid showed a stale number under "Latest" (ADR-0023).
 *
 * `updatedBy` was widened here by hand for the same reason and with the same result: the type promised an author
 * the schema stripped, because the server did not send one. It does now (GAP-002), so it comes off the wire
 * through `artifactSchema` like every other field and the hand-written declaration is gone.
 */
export type Artifact = Omit<ArtifactBase, 'tenantId' | 'scope' | 'basedOnPackVersion' | 'inFlightVersion' | 'inFlightStatus' | 'validated' | 'rowVersion' | 'createdAt' | 'updatedAt'> &
  Partial<Pick<ArtifactBase, 'tenantId' | 'basedOnPackVersion' | 'inFlightVersion' | 'inFlightStatus' | 'validated' | 'rowVersion' | 'createdAt' | 'updatedAt'>> & {
    scope?: ScopeRef | string;
    scopeId?: string | null;
  };

/** The version a designer opens: the one in flight, otherwise the live one. Null when the artifact has neither. */
export function artifactLatestVersion(a: Pick<Artifact, 'inFlightVersion' | 'currentPublishedVersion'>): number | null {
  return a.inFlightVersion ?? a.currentPublishedVersion;
}

/** Status of {@link artifactLatestVersion}: the in-flight stage, or PUBLISHED when only a live version exists. */
export function artifactLatestStatus(a: Pick<Artifact, 'inFlightStatus' | 'currentPublishedVersion'>): ArtifactStatus | null {
  return a.inFlightStatus ?? (a.currentPublishedVersion != null ? 'PUBLISHED' : null);
}

/** Human form of a scope instance: `TENANT`, `WAREHOUSE · <id>`. */
/**
 * True for a platform-level artifact (shipped by the platform, `tenantId` null). Read off the scope, which the
 * server ties to it (`DefinitionArtifact.create`: platform-level ⇔ PLATFORM scope), so it holds even where a
 * view leaves `tenantId` out. A tenant cannot write one; it overrides it with its own artifact under the same key.
 */
export function isPlatformArtifact(a: Pick<Artifact, 'scope'>): boolean {
  const scope: unknown = a.scope;
  return (typeof scope === 'object' && scope !== null ? (scope as { level?: string }).level : scope) === 'PLATFORM';
}

export function formatScope(scope: ScopeRef | string | null | undefined): string {
  if (!scope) return '—';
  if (typeof scope === 'string') return scope;
  return scope.scopeId ? `${scope.level} · ${scope.scopeId}` : scope.level;
}

/**
 * `ConfigurationSets.ConfigurationSetView`. A set is a CONFIGURATION_SET artifact: the values that are live are the
 * entries of its published version, which is why the view is an artifact plus those entries.
 */
export const configurationSetSchema = z.object({
  artifact: artifactSchema,
  scope: z.union([scopeRefSchema, z.string()]),
  publishedEntries: z.array(configurationEntrySchema),
});
export type ConfigurationSet = z.infer<typeof configurationSetSchema>;

const configurationKeys = {
  definitions: (prefix?: string) => queryKeys.custom('platform', 'configuration', 'definitions', prefix ?? null),
  definition: (key: string) => queryKeys.custom('platform', 'configuration', 'definitions', 'key', key),
  sets: (scopeLevel?: ScopeLevel, scopeId?: string) => queryKeys.custom('platform', 'configuration', 'sets', scopeLevel ?? null, scopeId ?? null),
  set: (id: string) => queryKeys.custom('platform', 'configuration', 'sets', 'id', id),
};

/**
 * Every registered definition (a plain array, not a page — the registry is small and read whole). `prefix` narrows
 * to keys starting with it. Definitions change only on deploy, so they are cached for a while.
 */
export function useConfigurationDefinitions(prefix?: string, options?: QueryOpts<ConfigurationDefinition[]>) {
  return useApiQuery<ConfigurationDefinition[]>(
    configurationKeys.definitions(prefix),
    (client, signal) =>
      client.get('/api/v1/platform/configuration/definitions', { query: { prefix }, schema: z.array(configurationDefinitionSchema), signal }),
    { staleTime: 5 * 60_000, ...(options ?? {}) },
  );
}

/** One definition by key. Keys are dotted (`audit.retention.days`); the backend maps the whole segment. */
export function useConfigurationDefinition(key: string | undefined, options?: QueryOpts<ConfigurationDefinition>) {
  return useApiQuery<ConfigurationDefinition>(
    configurationKeys.definition(key ?? ''),
    (client, signal) =>
      client.get(`/api/v1/platform/configuration/definitions/${encodeURIComponent(key ?? '')}`, { schema: configurationDefinitionSchema, signal }),
    { enabled: !!key, staleTime: 5 * 60_000, ...(options ?? {}) },
  );
}

/** Configuration sets, optionally of one scope instance (a plain array). */
export function useConfigurationSets(scope: { level?: ScopeLevel; scopeId?: string } = {}, options?: QueryOpts<ConfigurationSet[]>) {
  return useApiQuery<ConfigurationSet[]>(
    configurationKeys.sets(scope.level, scope.scopeId),
    (client, signal) =>
      client.get('/api/v1/platform/configuration/sets', {
        query: { scopeLevel: scope.level, scopeId: scope.scopeId },
        schema: z.array(configurationSetSchema),
        signal,
      }),
    options,
  );
}

/**
 * Parameters of `GET /configuration/effective`: exactly one of `key` (exact, or a `feature.*` pattern) and `prefix`
 * narrows the keys; `scope`/`scopeId` resolves the values as seen from that scope instance.
 */
export interface EffectiveConfigurationParams {
  key?: string;
  prefix?: string;
  scope?: ScopeLevel;
  scopeId?: string;
}

export function useEffectiveConfiguration(params: EffectiveConfigurationParams = {}) {
  return useApiQuery<EffectiveValue[]>(queryKeys.custom('platform', 'configuration', 'effective', params), (client, signal) =>
    client.get('/api/v1/platform/configuration/effective', { query: { ...params }, schema: z.array(effectiveValueSchema), signal }),
  );
}

export interface ConfigurationDraftCommand {
  scope: { level: ScopeLevel; scopeId?: string | null };
  entries: Array<{ key: string; value: unknown; effectiveFrom?: string | null; effectiveTo?: string | null }>;
  changeNote?: string;
}

/**
 * Changes a configuration value by drafting it into the scope's CONFIGURATION_SET artifact.
 *
 * There is deliberately no write on the resolved value: a value is owned by a set, and a set is metadata, so
 * changing one has to go through the draft → sandbox → published lifecycle that gives it review, versioning and
 * rollback. This call creates or updates the DRAFT; publishing it is the normal artifact transition, which is why
 * the response is an `ArtifactView` and not a value.
 */
export function useDraftConfigurationValues() {
  return usePost<Artifact, ConfigurationDraftCommand>('/api/v1/platform/configuration/sets/draft', { schema: artifactSchema });
}

/**
 * Artifacts. Filters: `kind`, `key` (prefix), `scopeLevel`, `scopeId`, `ownerPackId`, `status` (PUBLISHED = has a
 * live version, any other status = has a version in that stage) and free-text `q` over key and name. Sortable by
 * `key`, `name`, `kind`, `updatedAt`, `createdAt`.
 */
export const artifacts = defineListResource<Artifact>('platform', 'metadata/artifacts', artifactSchema as unknown as z.ZodType<Artifact>, '/api/v1/platform/metadata/artifacts');
export const artifactSortFields = ['key', 'name', 'kind', 'updatedAt', 'createdAt'] as const;

export function useArtifactList(kind: ArtifactKind | undefined, params: ListParams = {}) {
  const merged: ListParams = { ...params, filter: { ...(params.filter ?? {}), ...(kind ? { kind } : {}) } };
  return artifacts.useList(merged);
}

const artifactVersionsKey = (id: string | undefined) => queryKeys.custom('platform', 'artifacts', id, 'versions');

/** Every version of an artifact, oldest first. */
export function useArtifactVersions(id: string | undefined) {
  return useApiQuery<ArtifactVersion[]>(artifactVersionsKey(id), (client, signal) =>
    client.get(`/api/v1/platform/metadata/artifacts/${id}/versions`, { schema: z.array(artifactVersionSchema), signal }), { enabled: !!id });
}

/** One version by its **number** (`ArtifactVersion.version`), not by its id. */
// ---------- Custom fields (`AttributeController`, `/platform/metadata/attributes`) ----------

export const attributeTypes = ['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'DATETIME', 'ENUM', 'MULTI_ENUM', 'REFERENCE', 'JSON'] as const;

/** `DynamicAttributesApi.AttributeDefinitionView`: one published custom field of an entity, as the tenant sees it. */
export const attributeDefinitionSchema = z.object({
  entityCode: z.string(),
  code: z.string(),
  type: z.enum(attributeTypes),
  label: z.string(),
  required: z.boolean(),
  validation: z.unknown().nullable(),
  searchable: z.boolean(),
  inventoryDimension: z.boolean(),
  uiHints: z.unknown().nullable(),
  publishedVersion: z.number(),
  platformLevel: z.boolean(),
  permission: z.string().nullable(),
});
export type AttributeDefinition = z.infer<typeof attributeDefinitionSchema>;

/** `kernel.error.FieldError`. */
export const fieldErrorSchema = z.object({ field: z.string(), code: z.string(), message: z.string(), rejectedValue: z.unknown().nullable().optional() });
export type FieldErrorView = z.infer<typeof fieldErrorSchema>;

/** `GET /metadata/attributes/{entityCode}` (P-24): the custom fields in force for an entity — platform and tenant, published. */
export function useEntityAttributes(entityCode: string | undefined, options?: QueryOpts<AttributeDefinition[]>) {
  return useApiQuery<AttributeDefinition[]>(
    queryKeys.custom('platform', 'metadata', 'attributes', entityCode ?? ''),
    (client, signal) => client.get(`/api/v1/platform/metadata/attributes/${encodeURIComponent(entityCode ?? '')}`, { schema: z.array(attributeDefinitionSchema), signal }),
    { enabled: !!entityCode, ...(options ?? {}) },
  );
}

/** `POST /metadata/attributes/{entityCode}/validate` (P-24): the field errors candidate values would get; nothing is kept. */
export function useValidateEntityAttributes() {
  return useApiMutation<FieldErrorView[], { entityCode: string; values: Record<string, unknown> }>((client, { entityCode, values }) =>
    client.post<FieldErrorView[]>(`/api/v1/platform/metadata/attributes/${encodeURIComponent(entityCode)}/validate`, values, { schema: z.array(fieldErrorSchema) }),
  );
}

export function useArtifactVersion(id: string | undefined, versionNumber: number | null | undefined) {
  return useApiQuery<ArtifactVersion>(queryKeys.custom('platform', 'artifacts', id, 'versions', versionNumber), (client, signal) =>
    client.get(`/api/v1/platform/metadata/artifacts/${id}/versions/${versionNumber}`, { schema: artifactVersionSchema, signal }), { enabled: !!id && versionNumber != null });
}

/** Target statuses the lifecycle accepts from a client (`POST …/versions/{version}/transition`). */
export type TransitionTarget = 'SANDBOX' | 'TEST' | 'APPROVAL' | 'PUBLISHED' | 'DRAFT' | 'REJECTED';
export interface TransitionCommand { id: string; version: number; to: TransitionTarget; note?: string }
export function useTransitionArtifact() {
  return useApiMutation<ArtifactVersion, TransitionCommand>((client, { id, version, to, note }) =>
    client.post(`/api/v1/platform/metadata/artifacts/${id}/versions/${version}/transition`, { to, note }, { idempotencyKey: true, schema: artifactVersionSchema }),
  );
}

export interface CreateArtifactCommand {
  kind: ArtifactKind;
  key: string;
  name: string;
  description?: string;
  tags?: string[];
  scope?: { level: ScopeLevel; scopeId?: string | null };
  content: unknown;
  changeNote?: string;
}
export function useCreateArtifact() {
  return usePost<Artifact, CreateArtifactCommand>('/api/v1/platform/metadata/artifacts', { schema: artifactSchema });
}

/**
 * Saves content into the artifact's DRAFT (`PUT …/content`). The backend updates the in-flight DRAFT, or creates
 * DRAFT n+1 when the latest version is published or terminal; either way the answer is that version.
 */
export function useSaveArtifactDraft() {
  return useApiMutation<ArtifactVersion, { id: string; content: unknown; changeNote?: string; effectiveFrom?: string | null }>((client, { id, ...body }) =>
    client.put(`/api/v1/platform/metadata/artifacts/${id}/content`, body, { idempotencyKey: true, schema: artifactVersionSchema }),
  );
}

/** Creates a new DRAFT from the content of `toVersion`. */
export function useRollbackArtifact() {
  return useApiMutation<ArtifactVersion, { id: string; toVersion: number }>((client, { id, toVersion }) =>
    client.post(`/api/v1/platform/metadata/artifacts/${id}/rollback`, { toVersion }, { idempotencyKey: true, schema: artifactVersionSchema }),
  );
}

/**
 * `PUT /metadata/artifacts/{id}` (`ArtifactController.EnvelopeRequest`) — name, description and tags; `scope` only
 * while the artifact has never been published and is not pack-owned, otherwise `metadata.scope_locked` (G-104).
 * `tags` omitted keeps them; `scope` omitted keeps it. Audited as `metadata.artifact.update`.
 */
export interface ArtifactEnvelopeCommand { id: string; name: string; description?: string | null; tags?: string[]; scope?: { level: ScopeLevel; scopeId?: string | null } }
export function useUpdateArtifactEnvelope() {
  return useApiMutation<Artifact, ArtifactEnvelopeCommand>((client, { id, ...body }) =>
    client.put(`/api/v1/platform/metadata/artifacts/${id}`, body, { idempotencyKey: true, schema: artifactSchema as unknown as z.ZodType<Artifact> }),
  );
}

/** `ArtifactApi.DraftDeletion`: the draft removed, and whether the artifact went with it (its only version). */
export const draftDeletionSchema = z.object({ artifactId: z.string(), kind: artifactKindSchema, key: z.string(), version: z.number(), artifactDeleted: z.boolean() });
export type DraftDeletion = z.infer<typeof draftDeletionSchema>;
/**
 * `DELETE /metadata/artifacts/{id}/versions/{v}` (G-101). Only a DRAFT (`metadata.delete_not_draft`) of an artifact
 * that was never published (`metadata.artifact_published`), not pack-owned (`metadata.pack_managed`) and not in an
 * open change set (`metadata.artifact_in_change_set`). Audited as `metadata.artifact.delete_draft`.
 */
export function useDeleteArtifactDraft() {
  return useApiMutation<DraftDeletion, { id: string; version: number }>((client, { id, version }) =>
    client.delete(`/api/v1/platform/metadata/artifacts/${id}/versions/${version}`, { idempotencyKey: true, schema: draftDeletionSchema }),
  );
}

/**
 * `ArtifactApi.DirectPublishView` — whether "save and publish" would be accepted now (G-102). `allowed` is true
 * exactly when the kind's lifecycle policy collapses every stage (`requireApproval` and `requireSandboxTest` both
 * false), nothing is past DRAFT and no open change set holds the artifact; otherwise `refusalCode` is the problem
 * code the command answers with and `refusalDetail` says why.
 */
export const directPublishSchema = z.object({
  artifactId: z.string(),
  kind: artifactKindSchema,
  requireApproval: z.boolean(),
  requireSandboxTest: z.boolean(),
  allowed: z.boolean(),
  refusalCode: z.string().nullable(),
  refusalDetail: z.string().nullable(),
});
export type DirectPublish = z.infer<typeof directPublishSchema>;
export function useArtifactDirectPublish(id: string | undefined, options?: QueryOpts<DirectPublish>) {
  return useApiQuery<DirectPublish>(queryKeys.custom('platform', 'artifacts', id, 'direct-publish'), (client, signal) =>
    client.get(`/api/v1/platform/metadata/artifacts/${id}/direct-publish`, { schema: directPublishSchema, signal }), { enabled: !!id, ...(options ?? {}) });
}
/** `POST /metadata/artifacts/{id}/save-and-publish` — saves the draft and publishes it in one transaction, or neither. */
export function useSaveAndPublishArtifact() {
  return useApiMutation<ArtifactVersion, { id: string; content: unknown; changeNote?: string; note?: string }>((client, { id, ...body }) =>
    client.post(`/api/v1/platform/metadata/artifacts/${id}/save-and-publish`, body, { idempotencyKey: true, schema: artifactVersionSchema }),
  );
}

// ---------- Audit ----------
/** `ActorType` (`one.zagru.platform.kernel.actor.ActorType`); the audit view carries its name. */
export const auditActorTypes = ['USER', 'SERVICE', 'SYSTEM', 'AI_AGENT', 'WORKFLOW', 'DEVICE'] as const;
export type AuditActorType = (typeof auditActorTypes)[number];
/** `AuditOutcome`. */
export const auditOutcomes = ['SUCCESS', 'DENIED', 'FAILED'] as const;

/**
 * `AuditRecordView`. The actor is flat (`actorType`, `actorId`, `actorDisplay`); resource and warehouse are null for
 * records that are not about one resource (a denied request, a login). `details` is redacted server-side.
 */
/*
 * `AuditRecordView`, field for field.
 *
 * An `actor` object and a `resourceRef` string used to be declared here. The view has neither — it names
 * the actor with `actorType`/`actorId`/`actorDisplay` and the resource with
 * `resourceType`/`resourceId`/`resourceNumber` — so both parsed to `undefined` on every record, and the
 * audit screen's resource fallback chain ended in a branch that could never be taken.
 */
export const auditRecordSchema = z.object({
  id: z.string(),
  tenantId: z.string().optional(),
  occurredAt: z.string(),
  actorType: z.enum(auditActorTypes).optional(),
  actorId: z.string().optional(),
  actorDisplay: z.string().nullable().optional(),
  action: z.string(),
  resourceType: z.string().nullable().optional(),
  resourceId: z.string().nullable().optional(),
  resourceNumber: z.string().nullable().optional(),
  warehouseId: z.string().nullable().optional(),
  partyIds: z.array(z.string()).optional(),
  correlationId: z.string().optional(),
  workflowId: z.string().nullable().optional(),
  eventId: z.string().nullable().optional(),
  outcome: z.enum(auditOutcomes),
  details: z.record(z.string(), z.unknown()).optional(),
  before: z.unknown().optional(),
  after: z.unknown().optional(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  requestOrigin: z.string().optional(),
});
export type AuditRecord = z.infer<typeof auditRecordSchema>;

/**
 * Audit records. Filters: `actorId`, `action` (prefix), `resourceType`, `resourceId`, `from`/`to` (ISO instants),
 * `outcome`, `correlationId`. Sortable by `occurredAt`, `action`, `actorId`, `outcome`. There is no free-text `q`.
 */
export const auditRecords = defineListResource('platform', 'audit/records', auditRecordSchema, '/api/v1/platform/audit/records');
export const auditSortFields = ['occurredAt', 'action', 'actorId', 'outcome'] as const;

/** `AuditSubjectContribution.AuditSubject`: a subject whose own trail its readers may read. */
export const auditSubjectSchema = z.object({ subjectType: z.string(), resourceType: z.string(), readPermission: z.string() });
export type AuditSubject = z.infer<typeof auditSubjectSchema>;

/**
 * `GET /platform/audit/subjects/{subjectType}/{subjectId}` (G-030): one subject's audit trail, newest first, for whoever
 * may read the subject itself (a carrier with `transport:transport:read`) — no `audit:record:read` needed. 403 without
 * the subject's read permission, 404 for a subject type no module declares. Filters: `action` (prefix), `from`, `to`.
 */
export function useSubjectAuditHistory(subjectType: string, subjectId: string | undefined, params: { page?: number; size?: number; action?: string } = {}) {
  const { page = 0, size = 100, action } = params;
  return useApiQuery(
    queryKeys.custom('platform', 'audit', 'subjects', subjectType, subjectId ?? '', page, size, action ?? ''),
    (client, signal) =>
      client.get(`/api/v1/platform/audit/subjects/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId ?? '')}`, {
        query: { page, size, sort: 'occurredAt,desc', ...(action ? { 'filter[action]': action } : {}) },
        schema: pageSchema(auditRecordSchema),
        signal,
      }),
    { enabled: !!subjectId },
  );
}

/** Most records one audit export file holds (`AuditExports.MAX_ROWS`); the server says when the match was larger. */
export const AUDIT_EXPORT_MAX_ROWS = 50_000;

/**
 * `POST /audit/records/export` (`audit:record:export`, G-131): the governed search's whole matching set as CSV,
 * streamed by the server, redacted for leaving the platform (details/before/after re-redacted, IP address and user
 * agent partially masked), capped at {@link AUDIT_EXPORT_MAX_ROWS} and itself recorded in the trail. Takes the same
 * body as `/audit/records/search`; its page and size are ignored.
 */
export function useExportAuditRecords() {
  return useApiMutation<Blob, { body: unknown }>((client, { body }) => client.post<Blob>('/api/v1/platform/audit/records/export', body, { parse: 'blob' }));
}

export { toListQuery };

// ---------- Documents, labels and printing (PROD-011) ----------

/**
 * `platform/document`, as `DocumentApi`, `DocumentBindingApi` and `PrintQueueApi` publish it (ADR-0023).
 *
 * The shape of this module is ADR-0032's: a *binding* says which document type applies to which context
 * under which rule, a *template* renders it, and a *stored document* is what came out — immutable, with a
 * revision, because a document sent to a customs office cannot change afterwards. Nothing here decides
 * applicability: `POST /document-bindings/{contextType}/applicable` does, from the bindings and their ZEL
 * rules, and a client that worked it out for itself would be the `if (mode == ROAD) generateCMR()` the ADR
 * forbids, written in TypeScript.
 */

/** `OutputFormat`. PDF is not supported for labels; ZPL and SVG are the label formats. */
export const documentFormats = ['PDF', 'HTML', 'ZPL', 'CSV', 'SVG'] as const;
export type DocumentFormat = (typeof documentFormats)[number];

/** `domain.PrinterType`. */
/** `domain.PrinterType`. The server declares `PDF | ZPL`; the older names stay so recorded fixtures still parse. */
export const printerTypes = ['LABEL', 'DOCUMENT', 'THERMAL', 'LASER', 'PDF', 'ZPL'] as const;
/** `domain.PrintJobStatus`. */
export const printJobStatuses = ['QUEUED', 'CLAIMED', 'PRINTED', 'FAILED', 'CANCELLED'] as const;
export type PrintJobStatus = (typeof printJobStatuses)[number];

/**
 * `DocumentApi.DocumentView`.
 *
 * `revision` and `supersededBy` are ADR-0032's immutability: regenerating does not overwrite, it supersedes.
 * A view with `supersededBy` set is history — still downloadable, because what was sent is what was sent.
 */
export const storedDocumentSchema = z.object({
  id: z.string(),
  definitionKey: z.string().nullable(),
  definitionVersion: z.number(),
  format: z.enum(documentFormats),
  contentType: z.string(),
  size: z.number(),
  storageRef: z.string(),
  referenceKind: z.string().nullable(),
  referenceId: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  renderedAt: z.string(),
  renderedBy: z.string(),
  checksum: z.string(),
  fileName: z.string(),
  revision: z.number(),
  supersededBy: z.string().nullable(),
});
export type StoredDocument = z.infer<typeof storedDocumentSchema>;

/** `DocumentApi.DefinitionSummary`: a published template and the formats it can be rendered to. */
export const documentDefinitionSchema = z.object({
  kind: z.string(),
  key: z.string(),
  name: z.string(),
  version: z.number(),
  platformLevel: z.boolean(),
  formats: z.array(z.enum(documentFormats)),
});
export type DocumentDefinition = z.infer<typeof documentDefinitionSchema>;

/** `api.DocumentBinding`: which document type applies where, and when it is generated. */
export const documentBindingSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  documentType: z.string(),
  contextType: z.string(),
  templateKey: z.string().nullable(),
  appliesWhen: z.string().nullable(),
  generationPoint: z.string(),
  outputFormat: z.string().nullable(),
  copies: z.number(),
  enabled: z.boolean(),
  /** Which binding wins when two of the same document type apply; higher first. */
  priority: z.number(),
  /** Free-form settings a binding carries for its consumers (a preferred printer, a channel); opaque here. */
  metadata: z.record(z.string(), z.unknown()),
  /** W14: the language the document is issued in whoever generates it; null = the language of whoever generates it. */
  language: z.string().nullable().optional(),
  /** L-04 (D-318): the roles the document is for; empty means everyone. */
  audienceRoles: z.array(z.string()).optional(),
});
export type DocumentBinding = z.infer<typeof documentBindingSchema>;

/**
 * `DocumentBindingApi.ApplicableDocument`.
 *
 * `templateAvailable` is the one that matters operationally: a binding can apply and still have no template
 * resolvable through the customer → tenant → pack → default chain, and rendering it would fail. The screen
 * says so instead of offering a button that cannot work.
 */
export const applicableDocumentSchema = z.object({
  binding: documentBindingSchema,
  templateKey: z.string(),
  templateAvailable: z.boolean(),
});
export type ApplicableDocument = z.infer<typeof applicableDocumentSchema>;

/**
 * W14 `DocumentBindingApi.RecordDocuments`: the documents of one record, evaluated on the server from the facts its
 * own module supplies. `hidden` (the ones that do not apply, with the facts their rule reads) only reaches people who
 * may author bindings.
 */
export const recordDocumentsSchema = z.object({
  contextType: z.string(),
  recordId: z.string(),
  number: z.string(),
  referenceKind: z.string(),
  applicable: z.array(applicableDocumentSchema),
  hidden: z.array(z.object({ binding: documentBindingSchema, reason: z.string(), factsUsed: z.record(z.string(), z.unknown()) })).default([]),
});
export type RecordDocuments = z.infer<typeof recordDocumentsSchema>;

/** `DocumentFactsContribution.FactsContract`: the facts a context type's rules and templates can read. */
export const factsContractSchema = z.object({
  contextType: z.string(),
  description: z.string(),
  producer: z.string().nullable().optional(),
  paths: z.array(z.object({ path: z.string(), type: z.string(), description: z.string() })),
});
export type FactsContract = z.infer<typeof factsContractSchema>;

/** `GET /documents/facts/{contextType}` — the facts a binding rule can test (W14 rule editor). */
export function useDocumentFactsContract(contextType: string | undefined, options?: QueryOpts<FactsContract>) {
  return useApiQuery<FactsContract>(
    ['platform', 'documents', 'facts', contextType ?? ''],
    (client, signal) => client.get(`/api/v1/platform/documents/facts/${encodeURIComponent(contextType ?? '')}`, { schema: factsContractSchema, signal }),
    { enabled: !!contextType, retry: false, staleTime: 10 * 60_000, ...(options ?? {}) },
  );
}

/** `GET /document-bindings/{contextType}/records/{id}` — which documents apply to this record now, and why not the others. */
export function useRecordDocuments(contextType: string, recordId: string | undefined, options?: QueryOpts<RecordDocuments>, roles?: readonly string[]) {
  // L-04 (D-318): the viewer's roles narrow the offer to the documents meant for them; absent, every binding counts.
  const roleList = roles?.length ? [...roles].sort() : undefined;
  return useApiQuery<RecordDocuments>(
    ['platform', 'documents', 'record', contextType, recordId ?? '', roleList?.join(',') ?? ''],
    (client, signal) =>
      client.get(`/api/v1/platform/document-bindings/${encodeURIComponent(contextType)}/records/${encodeURIComponent(recordId ?? '')}`, {
        schema: recordDocumentsSchema,
        signal,
        query: roleList ? { roles: roleList } : undefined,
      }),
    { enabled: !!recordId, ...(options ?? {}) },
  );
}

/** `POST …/records/{id}/generate` — issue one applicable document from the record's server-side facts; stored under it. */
export function useGenerateRecordDocument(contextType: string, recordId: string) {
  return useApiMutation<RenderedDocument, { bindingKey: string }>((client, body) =>
    client.post<RenderedDocument>(`/api/v1/platform/document-bindings/${encodeURIComponent(contextType)}/records/${encodeURIComponent(recordId)}/generate`, body, {
      idempotencyKey: true,
      schema: renderedDocumentSchema,
    }),
  );
}

/** `GET /documents/{id}/content` — a stored document's bytes, to open in a new tab. */
export function useStoredDocumentContent() {
  return useApiMutation<Blob, { id: string }>((client, { id }) => client.get<Blob>(`/api/v1/platform/documents/${encodeURIComponent(id)}/content`, { parse: 'blob' }));
}

/** An uploaded document: a stored document whose format may also be PNG or JPEG (`OutputFormat`, uploads only). */
export const uploadedDocumentSchema = storedDocumentSchema.extend({ format: z.string() });
export type UploadedDocument = z.infer<typeof uploadedDocumentSchema>;

/** What `POST /platform/documents/upload` takes: the file, and optionally the record it belongs to. */
export interface UploadDocumentVars {
  file: File;
  referenceKind?: string | null;
  referenceId?: string | null;
  referenceNumber?: string | null;
}

/**
 * Stores an uploaded file (PDF, PNG, JPEG, SVG, CSV or HTML, at most 20 MB) in the document store (multipart) and
 * returns its view; the caller keeps the id as evidence on its own record.
 */
export function useUploadDocument() {
  return useApiMutation<UploadedDocument, UploadDocumentVars>((client, { file, referenceKind, referenceId, referenceNumber }) => {
    const form = new FormData();
    form.append('file', file, file.name);
    const query: Record<string, string> = {};
    if (referenceKind && referenceId) {
      query.referenceKind = referenceKind;
      query.referenceId = referenceId;
      if (referenceNumber) query.referenceNumber = referenceNumber;
    }
    return client.post<UploadedDocument>('/api/v1/platform/documents/upload', form, { schema: uploadedDocumentSchema, query });
  });
}

export const documentBundleSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  contextType: z.string(),
  appliesWhen: z.string().nullable(),
  documents: z.array(z.object({ documentType: z.string(), required: z.boolean(), sequence: z.number() })),
  combineIntoOneFile: z.boolean(),
  enabled: z.boolean(),
});

/** `DocumentBindingApi.ApplicableBundle`: `missingRequired` names the document types that would be absent. */
export const applicableBundleSchema = z.object({
  bundle: documentBundleSchema,
  documents: z.array(applicableDocumentSchema),
  missingRequired: z.array(z.string()),
});
export type ApplicableBundle = z.infer<typeof applicableBundleSchema>;

/** `PrintQueueApi.PrinterView`. `connection` is free-form per printer type (host, port, queue name). */
export const printerSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.enum(printerTypes),
  warehouseId: z.string().nullable(),
  connection: z.record(z.string(), z.unknown()),
  active: z.boolean(),
  version: z.number(),
});
export type Printer = z.infer<typeof printerSchema>;

/** `PrintQueueApi.PrintJobView`. `claimedBy` is the print agent that took it off the queue. */
export const printJobSchema = z.object({
  id: z.string(),
  printerCode: z.string(),
  documentId: z.string(),
  format: z.enum(documentFormats),
  copies: z.number(),
  status: z.enum(printJobStatuses),
  attempts: z.number(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  claimedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  claimedBy: z.string().nullable(),
});
export type PrintJob = z.infer<typeof printJobSchema>;

/** `DocumentController.RenderResponse`. `documentId` is null when the render was not stored. */
export const renderedDocumentSchema = z.object({
  documentId: z.string().nullable(),
  definitionKey: z.string(),
  definitionVersion: z.number(),
  format: z.enum(documentFormats),
  contentType: z.string(),
  fileName: z.string(),
  size: z.number(),
});
export type RenderedDocument = z.infer<typeof renderedDocumentSchema>;

/** The context types a binding can be declared against (`DocumentContexts.ALL`). */
export function useDocumentContextTypes(options?: QueryOpts<string[]>) {
  return useApiQuery<string[]>(
    ['platform', 'document-bindings', 'context-types'],
    (client, signal) => client.get<string[]>('/api/v1/platform/document-bindings/context-types', { schema: z.array(z.string()), signal }),
    options,
  );
}

/** Every binding, or those of one context type. A read: which documents *could* apply anywhere. */
export function useDocumentBindings(contextType?: string, options?: QueryOpts<DocumentBinding[]>) {
  return useApiQuery<DocumentBinding[]>(
    ['platform', 'document-bindings', contextType ?? 'all'],
    (client, signal) =>
      client.get<DocumentBinding[]>('/api/v1/platform/document-bindings', {
        query: contextType ? { contextType } : {},
        schema: z.array(documentBindingSchema),
        signal,
      }),
    options,
  );
}

/**
 * `POST /document-bindings/{contextType}/applicable` — which documents apply to *this* record.
 *
 * A read behind a POST, because the request is a document: the facts the ZEL rule is evaluated against, and
 * the resolution context the template chain is walked in. This is the endpoint ADR-0032 exists to make the
 * only answer: the client sends facts and is told what applies, rather than deciding.
 */
export interface ApplicableRequest {
  contextType: string;
  facts?: Record<string, unknown>;
  generationPoint?: string | null;
  context?: { warehouseId?: string | null; partyId?: string | null };
}

export function useApplicableDocuments(request: ApplicableRequest | null, options?: QueryOpts<ApplicableDocument[]>) {
  return useApiQuery<ApplicableDocument[]>(
    ['platform', 'document-bindings', 'applicable', request?.contextType ?? '', JSON.stringify(request?.facts ?? {})],
    (client, signal) =>
      client.post<ApplicableDocument[]>(
        `/api/v1/platform/document-bindings/${encodeURIComponent(request!.contextType)}/applicable`,
        { facts: request!.facts ?? {}, generationPoint: request!.generationPoint ?? null, context: request!.context ?? null },
        { schema: z.array(applicableDocumentSchema), signal },
      ),
    { enabled: !!request?.contextType, ...(options ?? {}) },
  );
}

/** The same question for bundles: the named, ordered sets ADR-0032 makes configuration. */
export function useApplicableBundles(request: ApplicableRequest | null, options?: QueryOpts<ApplicableBundle[]>) {
  return useApiQuery<ApplicableBundle[]>(
    ['platform', 'document-bindings', 'bundles', request?.contextType ?? '', JSON.stringify(request?.facts ?? {})],
    (client, signal) =>
      client.post<ApplicableBundle[]>(
        `/api/v1/platform/document-bindings/${encodeURIComponent(request!.contextType)}/bundles`,
        { facts: request!.facts ?? {}, context: request!.context ?? null },
        { schema: z.array(applicableBundleSchema), signal },
      ),
    { enabled: !!request?.contextType, ...(options ?? {}) },
  );
}

/** The published templates, with the formats each can produce. */
export function useDocumentDefinitions(options?: QueryOpts<DocumentDefinition[]>) {
  return useApiQuery<DocumentDefinition[]>(
    ['platform', 'documents', 'definitions'],
    (client, signal) => client.get<DocumentDefinition[]>('/api/v1/platform/documents/definitions', { schema: z.array(documentDefinitionSchema), signal }),
    options,
  );
}

/** `GET /documents?referenceKind=&referenceId=` — what has already been generated for one record. */
export function useDocumentsFor(reference: { kind: string; id: string } | null, options?: QueryOpts<StoredDocument[]>) {
  return useApiQuery<StoredDocument[]>(
    ['platform', 'documents', 'for', reference?.kind ?? '', reference?.id ?? ''],
    (client, signal) =>
      client.get<StoredDocument[]>('/api/v1/platform/documents', {
        query: { referenceKind: reference!.kind, referenceId: reference!.id },
        schema: z.array(storedDocumentSchema),
        signal,
      }),
    { enabled: !!reference?.id, ...(options ?? {}) },
  );
}

export interface RenderDocumentInput {
  definitionKey: string;
  data?: Record<string, unknown>;
  format?: DocumentFormat | null;
  locale?: string | null;
  reference?: { kind: string; id: string; number?: string | null } | null;
  fileName?: string | null;
  copies?: number | null;
}

function renderBody(input: RenderDocumentInput) {
  return {
    definitionKey: input.definitionKey,
    data: input.data ?? {},
    locale: input.locale ?? null,
    format: input.format ?? null,
    reference: input.reference ? { kind: input.reference.kind, id: input.reference.id, number: input.reference.number ?? null } : null,
    fileName: input.fileName ?? null,
    copies: input.copies ?? 1,
  };
}

/**
 * `POST /documents/render` — renders and **stores**, which is the manual generation mode of ADR-0032.
 *
 * Storing is what makes it a record: the bytes, the template version they came from and the reference they
 * belong to are kept, so what was sent can be shown again exactly. Regenerating the same document later
 * supersedes this one rather than replacing it.
 */
export function useRenderDocument() {
  return useApiMutation<RenderedDocument, RenderDocumentInput>((client, input) =>
    client.post<RenderedDocument>('/api/v1/platform/documents/render', { ...renderBody(input), store: true }, {
      idempotencyKey: true,
      schema: renderedDocumentSchema,
    }),
  );
}

/**
 * `POST /documents/render/content` — the same render, returned as bytes and **not** stored.
 *
 * For looking before committing. Nothing is recorded, so nothing is claimed to have been produced; the blob
 * is the caller's to show or discard.
 */
export function useRenderDocumentContent() {
  return useApiMutation<Blob, RenderDocumentInput>((client, input) =>
    client.post<Blob>('/api/v1/platform/documents/render/content', { ...renderBody(input), store: false }, { parse: 'blob' }),
  );
}

/**
 * `POST /documents/preview` (`DocumentController.PreviewBody`) — renders a DOCUMENT or LABEL definition that is not
 * published yet (the designer's draft) against sample data; nothing is stored. The answer is the rendered bytes.
 */
export interface DocumentPreviewInput { kind: 'DOCUMENT' | 'LABEL'; definition: unknown; data: Record<string, unknown>; locale?: string | null; format?: DocumentFormat | null }
export function usePreviewDocument() {
  return useApiMutation<Blob, DocumentPreviewInput>((client, input) =>
    client.post<Blob>('/api/v1/platform/documents/preview', { kind: input.kind, definition: input.definition, data: input.data, locale: input.locale ?? null, format: input.format ?? null }, { parse: 'blob' }),
  );
}

/**
 * `DELETE /documents/{id}` — sensitive (`document:document:delete`).
 *
 * ADR-0032 makes generated documents immutable, so this is not "correct it": it removes the evidence of
 * what was produced. Correcting means rendering again, which supersedes.
 */
export function useDeleteDocument() {
  return usePost<void, { id: string }>((v) => `/api/v1/platform/documents/${v.id}`, { method: 'delete' });
}

/** `GET /documents/print-jobs` — the queue, optionally for one printer or one status. */
export function usePrintJobs(filter: { printerCode?: string; status?: PrintJobStatus } = {}, options?: QueryOpts<PrintJob[]>) {
  return useApiQuery<PrintJob[]>(
    ['platform', 'print-jobs', filter.printerCode ?? '', filter.status ?? ''],
    (client, signal) =>
      client.get<PrintJob[]>('/api/v1/platform/documents/print-jobs', {
        query: { ...(filter.printerCode ? { printerCode: filter.printerCode } : {}), ...(filter.status ? { status: filter.status } : {}) },
        schema: z.array(printJobSchema),
        signal,
      }),
    options,
  );
}

/**
 * `POST /documents/print-jobs` — puts a document on a printer's queue.
 *
 * Either an existing stored document, or a render executed and stored at enqueue time. A print agent claims
 * it through `GET /next` and acknowledges it; nothing here prints anything directly.
 */
export function useEnqueuePrintJob() {
  return useApiMutation<PrintJob, { printerCode: string; documentId?: string | null; render?: RenderDocumentInput | null; copies?: number | null }>(
    (client, { printerCode, documentId, render, copies }) =>
      client.post<PrintJob>(
        '/api/v1/platform/documents/print-jobs',
        { printerCode, documentId: documentId ?? null, render: render ? { ...renderBody(render), store: true } : null, copies: copies ?? 1 },
        { idempotencyKey: true, schema: printJobSchema },
      ),
  );
}

/** `POST /documents/print-jobs/{id}/cancel` — takes it off the queue before an agent claims it. */
export function useCancelPrintJob() {
  return usePost<PrintJob, { id: string }>((v) => `/api/v1/platform/documents/print-jobs/${v.id}/cancel`, { schema: printJobSchema });
}

/**
 * `GET /documents/print-jobs/summary?warehouseId=` (Wave 4 H-05) — the printers KPI strip in one call, over the printers
 * of a site (their `warehouseId`) and their jobs, or everything. `byStatus`: jobs per `PrintJobStatus` (0 when none);
 * `queued`/`claimed`/`failed`: the same counts named; `oldestQueuedAt`: when the oldest QUEUED job was created (its
 * age is the "oldest waiting" KPI), `null` when none waits; `printers`: registered printers; `inactivePrinters`:
 * `active = false`.
 */
export const printJobSummarySchema = z.object({
  byStatus: z.record(z.string(), z.number()),
  queued: z.number(),
  claimed: z.number(),
  failed: z.number(),
  oldestQueuedAt: z.string().nullable(),
  printers: z.number(),
  inactivePrinters: z.number(),
});
export type PrintJobSummary = z.infer<typeof printJobSummarySchema>;

export function usePrintJobSummary(warehouseId?: string | null, options?: QueryOpts<PrintJobSummary>) {
  const query: Record<string, string> = warehouseId ? { warehouseId } : {};
  return useApiQuery<PrintJobSummary>(
    ['platform', 'print-jobs', 'summary', query],
    (client, signal) => client.get<PrintJobSummary>('/api/v1/platform/documents/print-jobs/summary', { query, schema: printJobSummarySchema, signal }),
    { staleTime: 30_000, ...(options ?? {}) },
  );
}

/**
 * `GET /documents/summary` (Wave 4 H-05) — the documents KPI strip in one call, tenant-wide. `total`: stored documents;
 * `current`: not superseded (state CURRENT); `superseded`: replaced by a later revision; `renderedToday`: rendered or
 * uploaded since the start of today (server zone); `failedPrintJobs`: FAILED print jobs (links to Facilities › Printers).
 */
export const documentSummarySchema = z.object({
  total: z.number(),
  current: z.number(),
  superseded: z.number(),
  renderedToday: z.number(),
  failedPrintJobs: z.number(),
});
export type DocumentSummary = z.infer<typeof documentSummarySchema>;

export function useDocumentSummary(options?: QueryOpts<DocumentSummary>) {
  return useApiQuery<DocumentSummary>(
    ['platform', 'documents', 'summary'],
    (client, signal) => client.get<DocumentSummary>('/api/v1/platform/documents/summary', { schema: documentSummarySchema, signal }),
    { staleTime: 30_000, ...(options ?? {}) },
  );
}

/** `GET /documents/printers`, optionally scoped to a site. */
export function usePrinters(warehouseId?: string | null, options?: QueryOpts<Printer[]>) {
  return useApiQuery<Printer[]>(
    ['platform', 'printers', warehouseId ?? 'all'],
    (client, signal) =>
      client.get<Printer[]>('/api/v1/platform/documents/printers', {
        query: warehouseId ? { warehouseId } : {},
        schema: z.array(printerSchema),
        signal,
      }),
    options,
  );
}

export interface RegisterPrinterInput {
  code: string;
  name: string;
  type: (typeof printerTypes)[number];
  warehouseId?: string | null;
  connection?: Record<string, unknown>;
  active?: boolean;
}

/** `POST /documents/printers` — admin (`document:printer:manage`). The code is how everything refers to it. */
export function useRegisterPrinter() {
  return usePost<Printer, RegisterPrinterInput>('/api/v1/platform/documents/printers', { schema: printerSchema });
}

/** `PUT /documents/printers/{code}` — `UpdatePrinter` reads every null as "leave it alone". */
export function useUpdatePrinter() {
  return usePost<Printer, { code: string; name?: string | null; warehouseId?: string | null; connection?: Record<string, unknown> | null; active?: boolean | null }>(
    (v) => `/api/v1/platform/documents/printers/${encodeURIComponent(v.code)}`,
    { method: 'put', schema: printerSchema },
  );
}

// ---------- Notifications (PROD-012) ----------

/**
 * `platform/notification`, as `NotificationApi` publishes it.
 *
 * The inbox is the half nobody could reach. Notifications are *produced* all over the product —
 * `HumanTaskCreatedHandler` when a workflow needs a person, `ExceptionNotifier` when the control tower
 * raises one, the control tower's own notify-customer and notify-broker actions — and every one of them
 * landed in an inbox with no reader. The bell in the shell points at control tower exceptions, which is a
 * different thing: an exception is a state of the world, a notification is something addressed to you.
 */

/** `api.Channel`. IN_APP is the inbox; the rest leave the product. */
export const notificationChannels = ['IN_APP', 'EMAIL', 'WEBHOOK', 'PUSH'] as const;
export type NotificationChannel = (typeof notificationChannels)[number];
/** `api.Severity`. */
export const notificationSeverities = ['INFO', 'WARNING', 'CRITICAL'] as const;
/**
 * `api.NotificationKind` (G-006): what a notification is to its reader — the inbox tabs. Declared by the definition
 * or derived from its severity (WARNING/CRITICAL = ALERT, INFO = SYSTEM); recorded at send time. Not `category`,
 * which is the definition's free-form grouping that preferences opt out of.
 */
export const notificationKinds = ['ALERT', 'MENTION', 'SYSTEM'] as const;
export type NotificationKind = (typeof notificationKinds)[number];
/** `domain.DeliveryStatus`. SKIPPED is a recipient who opted out, not a failure. */
export const deliveryStatuses = ['QUEUED', 'SENT', 'FAILED', 'SKIPPED'] as const;

/** `NotificationApi.InboxItemView`. `readAt` null is unread. */
export const inboxItemSchema = z.object({
  id: z.string(),
  notificationId: z.string(),
  definitionKey: z.string(),
  category: z.string(),
  severity: z.enum(notificationSeverities),
  kind: z.enum(notificationKinds),
  subject: z.string(),
  body: z.string(),
  referenceKind: z.string().nullable(),
  referenceId: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type InboxItem = z.infer<typeof inboxItemSchema>;

/** `NotificationApi.DeliveryView`: one attempt to reach one recipient on one channel. */
export const notificationDeliverySchema = z.object({
  id: z.string(),
  notificationId: z.string(),
  channel: z.enum(notificationChannels),
  recipient: z.string(),
  status: z.enum(deliveryStatuses),
  attempts: z.number(),
  lastError: z.string().nullable(),
  sentAt: z.string().nullable(),
  nextAttemptAt: z.string().nullable(),
});
export type NotificationDelivery = z.infer<typeof notificationDeliverySchema>;

/** `NotificationApi.PreferenceView`: one opt-out, per category and channel. */
export const notificationPreferenceSchema = z.object({
  userId: z.string(),
  category: z.string(),
  channel: z.enum(notificationChannels),
  optedOut: z.boolean(),
});
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;

/** The viewer's inbox. `NotificationService.inbox` reads `filter[unread]` and `filter[kind]` (ALERT | MENTION | SYSTEM). */
export const notificationInbox = defineListOnlyResource(
  'platform',
  'notifications/inbox',
  inboxItemSchema,
  '/api/v1/platform/notifications/inbox',
);

/** `GET /notifications/inbox/unread-count` — what a badge is allowed to show. */
export function useUnreadNotificationCount(options?: QueryOpts<number>) {
  return useApiQuery<number>(
    ['platform', 'notifications', 'unread-count'],
    (client, signal) =>
      client
        .get<{ unread: number }>('/api/v1/platform/notifications/inbox/unread-count', { schema: z.object({ unread: z.number() }), signal })
        .then((r) => r.unread),
    options,
  );
}

export const unreadCountsSchema = z.object({ unread: z.number(), byKind: z.record(z.enum(notificationKinds), z.number()) });
export type UnreadCounts = z.infer<typeof unreadCountsSchema>;

/** `GET /notifications/inbox/unread-count`, with the per-kind counts the inbox tabs badge themselves with (G-006). */
export function useUnreadNotificationCounts(options?: QueryOpts<UnreadCounts>) {
  return useApiQuery<UnreadCounts>(
    ['platform', 'notifications', 'unread-count', 'by-kind'],
    (client, signal) => client.get<UnreadCounts>('/api/v1/platform/notifications/inbox/unread-count', { schema: unreadCountsSchema, signal }),
    options,
  );
}

/** `POST /notifications/inbox/{id}/read`. */
export function useMarkNotificationRead() {
  return usePost<InboxItem, { id: string }>((v) => `/api/v1/platform/notifications/inbox/${v.id}/read`, { schema: inboxItemSchema });
}

/** `POST /notifications/inbox/read-all` — returns how many it marked, which is the only honest confirmation. */
export function useMarkAllNotificationsRead() {
  return useApiMutation<{ marked: number }, void>((client) =>
    client.post<{ marked: number }>('/api/v1/platform/notifications/inbox/read-all', undefined, { schema: z.object({ marked: z.number() }) }),
  );
}

/** `GET /notifications/preferences` — only the opt-outs this viewer has set. */
export function useNotificationPreferences(options?: QueryOpts<NotificationPreference[]>) {
  return useApiQuery<NotificationPreference[]>(
    ['platform', 'notifications', 'preferences'],
    (client, signal) =>
      client.get<NotificationPreference[]>('/api/v1/platform/notifications/preferences', { schema: z.array(notificationPreferenceSchema), signal }),
    options,
  );
}

/**
 * `PUT /notifications/preferences` — opts one category and channel in or out, for the viewer.
 *
 * Opting out does not stop the notification being raised; it stops it being *delivered* to you on that
 * channel, and the delivery is recorded as SKIPPED rather than vanishing. So an audit still shows that you
 * were a recipient and why nothing was sent.
 */
export function useUpdateNotificationPreference() {
  return usePost<NotificationPreference, { category: string | null; channel: NotificationChannel; optedOut: boolean }>(
    '/api/v1/platform/notifications/preferences',
    { method: 'put', schema: notificationPreferenceSchema },
  );
}

/** `NotificationApi.PreferenceDefaultView` (G-126): the tenant's default for one category and channel. */
export const notificationPreferenceDefaultSchema = z.object({
  category: z.string(),
  channel: z.enum(notificationChannels),
  optedOut: z.boolean(),
});
export type NotificationPreferenceDefault = z.infer<typeof notificationPreferenceDefaultSchema>;

/** `GET /notifications/preferences/defaults` (`notification:preference:manage`). */
export function useNotificationPreferenceDefaults(options?: QueryOpts<NotificationPreferenceDefault[]>) {
  return useApiQuery<NotificationPreferenceDefault[]>(
    ['platform', 'notifications', 'preferences', 'defaults'],
    (client, signal) =>
      client.get<NotificationPreferenceDefault[]>('/api/v1/platform/notifications/preferences/defaults', { schema: z.array(notificationPreferenceDefaultSchema), signal }),
    options,
  );
}

/**
 * `PUT /notifications/preferences/defaults` — the tenant default for one category and channel. It applies to every
 * recipient who has set nothing of their own for that channel; anyone's own choice still wins. Audited.
 */
export function useUpdateNotificationPreferenceDefault() {
  return usePost<NotificationPreferenceDefault, { category: string | null; channel: NotificationChannel; optedOut: boolean }>(
    '/api/v1/platform/notifications/preferences/defaults',
    { method: 'put', schema: notificationPreferenceDefaultSchema },
  );
}

/** `GET /notifications/preferences/users/{userId}` — one person's own opt-outs, for an administrator. */
export function useUserNotificationPreferences(userId: string | undefined, options?: QueryOpts<NotificationPreference[]>) {
  return useApiQuery<NotificationPreference[]>(
    ['platform', 'notifications', 'preferences', 'user', userId ?? ''],
    (client, signal) =>
      client.get<NotificationPreference[]>(`/api/v1/platform/notifications/preferences/users/${encodeURIComponent(userId ?? '')}`, {
        schema: z.array(notificationPreferenceSchema),
        signal,
      }),
    { enabled: !!userId, ...(options ?? {}) },
  );
}

/** `PUT /notifications/preferences/users/{userId}` — sets one of that person's preferences on their behalf. Audited. */
export function useUpdateUserNotificationPreference() {
  return useApiMutation<NotificationPreference, { userId: string; category: string | null; channel: NotificationChannel; optedOut: boolean }>((client, v) =>
    client.put<NotificationPreference>(
      `/api/v1/platform/notifications/preferences/users/${encodeURIComponent(v.userId)}`,
      { category: v.category, channel: v.channel, optedOut: v.optedOut },
      { idempotencyKey: true, schema: notificationPreferenceSchema },
    ),
  );
}

/** `ReleaseNotesApi.ReleaseNoteView` (G-007): one version's notes, as authored markdown. */
export const releaseNoteSchema = z.object({ version: z.string(), date: z.string(), title: z.string(), markdown: z.string() });
export type ReleaseNote = z.infer<typeof releaseNoteSchema>;

/** `GET /platform/release-notes` — the release notes shipped with this build, newest first. Any signed-in user. */
export function useReleaseNotes(options?: QueryOpts<ReleaseNote[]>) {
  return useApiQuery<ReleaseNote[]>(
    ['platform', 'release-notes'],
    (client, signal) => client.get<ReleaseNote[]>('/api/v1/platform/release-notes', { schema: z.array(releaseNoteSchema), signal }),
    { staleTime: 60 * 60_000, ...(options ?? {}) },
  );
}

/** `GET /notifications/{id}/deliveries` — every attempt on every channel for one notification. */
export function useNotificationDeliveries(notificationId: string | undefined, options?: QueryOpts<NotificationDelivery[]>) {
  return useApiQuery<NotificationDelivery[]>(
    ['platform', 'notifications', notificationId ?? '', 'deliveries'],
    (client, signal) =>
      client.get<NotificationDelivery[]>(`/api/v1/platform/notifications/${encodeURIComponent(notificationId ?? '')}/deliveries`, {
        schema: z.array(notificationDeliverySchema),
        signal,
      }),
    { enabled: !!notificationId, ...(options ?? {}) },
  );
}

/**
 * `POST /notifications/deliveries/{id}/retry` — sends a failed delivery again.
 *
 * Only a failed one: a queued delivery has not been tried, and a sent one arrived. Retrying re-queues it for
 * the dispatcher rather than sending inline, so the answer is "queued again", not "delivered".
 */
export function useRetryNotificationDelivery() {
  return usePost<NotificationDelivery, { id: string }>((v) => `/api/v1/platform/notifications/deliveries/${v.id}/retry`, {
    schema: notificationDeliverySchema,
  });
}

/**
 * `POST /notifications` — sends one from a published definition.
 *
 * Almost every notification in this product is raised by something that happened. This is the exception: an
 * administrator proving that a definition they have just published actually reaches somebody, on the
 * channels it declares. That is why the screen that uses it is an admin surface and says "test", not a
 * general-purpose message composer — the product has no such thing and should not grow one here.
 */
export interface SendNotificationInput {
  definitionKey: string;
  userIds?: string[];
  roles?: string[];
  emails?: string[];
  data?: Record<string, unknown>;
  channels?: NotificationChannel[] | null;
  /** L-04: the language the message is written in (a language tag); absent means each recipient's own. */
  locale?: string | null;
  /** L-04: the record the message is about, so it shows in the record's history. */
  reference?: { kind: string; id: string; number?: string | null } | null;
  /** L-04: stored documents sent as attachments (e-mail). */
  attachmentDocumentIds?: string[];
}

export const notificationSchema = z.object({
  id: z.string(),
  definitionKey: z.string(),
  category: z.string(),
  severity: z.enum(notificationSeverities),
  kind: z.enum(notificationKinds),
  subject: z.string(),
  body: z.string(),
  data: z.record(z.string(), z.unknown()),
  referenceKind: z.string().nullable(),
  referenceId: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  throttled: z.boolean(),
  deliveries: z.array(notificationDeliverySchema),
});
export type Notification = z.infer<typeof notificationSchema>;

export function useSendNotification() {
  return useApiMutation<Notification, SendNotificationInput>((client, input) =>
    client.post<Notification>(
      '/api/v1/platform/notifications',
      {
        definitionKey: input.definitionKey,
        recipients: { userIds: input.userIds ?? [], roles: input.roles ?? [], emails: input.emails ?? [], partyIds: [], webhookUrls: [] },
        data: input.data ?? {},
        channels: input.channels ?? null,
        locale: input.locale ?? null,
        reference: input.reference ?? null,
        attachmentDocumentIds: input.attachmentDocumentIds ?? [],
      },
      { idempotencyKey: true, schema: notificationSchema },
    ),
  );
}

// ---------- Localization (PROD-013, GAP-011) ----------

/**
 * `platform/localization`: the tenant's own translations, and the rates it converts money at.
 *
 * GAP-011 is what makes the translations half urgent rather than tidy. The shell runs with `lang="tr"` and
 * the design system uppercases small labels in CSS, so an English word that has no Turkish translation is
 * uppercased by Turkish rules: `Matching` becomes `MATCHİNG`, with a dotted capital I. The casing rule is
 * correct and the missing translation is the defect — so the fix is a surface that shows which keys are
 * missing in which locale and lets somebody supply them.
 */

/** `LocalizationApi.TranslationView`. `text` is the tenant's override of a platform label. */
export const translationSchema = z.object({
  namespace: z.string(),
  key: z.string(),
  locale: z.string(),
  text: z.string(),
  /** W12: `OVERRIDDEN` — the tenant's own wording; `REVIEWED` — the shipped text, read and accepted (`text` = what was accepted). */
  status: z.enum(['OVERRIDDEN', 'REVIEWED']).catch('OVERRIDDEN'),
});
export type Translation = z.infer<typeof translationSchema>;

/** The namespace the tenant's wording of the product's own interface lives in (key = the catalogue key). */
export const UI_TRANSLATION_NAMESPACE = 'ui';

/** `ExchangeRates.ExchangeRateView`. One rate, one pair, one day. */
export const exchangeRateSchema = z.object({
  base: z.string(),
  quote: z.string(),
  rateDate: z.string(),
  rate: z.number(),
  source: z.string(),
});
export type ExchangeRate = z.infer<typeof exchangeRateSchema>;

export const localesSchema = z.object({ supported: z.array(z.string()), defaultLocale: z.string() });

/** `GET /localization/locales` — which locales this deployment supports, and which it falls back to. */
export function useLocales(options?: QueryOpts<z.infer<typeof localesSchema>>) {
  return useApiQuery<z.infer<typeof localesSchema>>(
    ['platform', 'localization', 'locales'],
    (client, signal) => client.get('/api/v1/platform/localization/locales', { schema: localesSchema, signal }),
    options,
  );
}

/** `GET /localization/translations`, narrowed by namespace and locale. */
export function useTranslations(filter: { namespace?: string; locale?: string } = {}, options?: QueryOpts<Translation[]>) {
  return useApiQuery<Translation[]>(
    ['platform', 'localization', 'translations', filter.namespace ?? '', filter.locale ?? ''],
    (client, signal) =>
      client.get<Translation[]>('/api/v1/platform/localization/translations', {
        query: { ...(filter.namespace ? { namespace: filter.namespace } : {}), ...(filter.locale ? { locale: filter.locale } : {}) },
        schema: z.array(translationSchema),
        signal,
      }),
    options,
  );
}

/**
 * `PATCH /localization/translations` — upserts several at once.
 *
 * A null or blank `text` **deletes** the entry rather than storing an empty string, which is the server's
 * documented behaviour and the only sane one: an empty translation would render as nothing at all, which is
 * worse than falling back to the platform label.
 */
export function usePatchTranslations() {
  return useApiMutation<Translation[], { translations: Array<{ namespace: string; key: string; locale: string; text: string | null; status?: Translation['status'] }> }>(
    (client, body) =>
      client.patch<Translation[]>('/api/v1/platform/localization/translations', body, {
        idempotencyKey: true,
        schema: z.array(translationSchema),
      }),
  );
}

/** `GET /localization/exchange-rates`, optionally for one pair. */
export function useExchangeRates(filter: { base?: string; quote?: string } = {}, options?: QueryOpts<ExchangeRate[]>) {
  return useApiQuery<ExchangeRate[]>(
    ['platform', 'localization', 'exchange-rates', filter.base ?? '', filter.quote ?? ''],
    (client, signal) =>
      client.get<ExchangeRate[]>('/api/v1/platform/localization/exchange-rates', {
        query: { ...(filter.base ? { base: filter.base } : {}), ...(filter.quote ? { quote: filter.quote } : {}) },
        schema: z.array(exchangeRateSchema),
        signal,
      }),
    options,
  );
}

/** `LocalizationController.RateResponse`: the rate a conversion would use; `rate` is null when none is known. */
export const exchangeRateOnSchema = z.object({ from: z.string(), to: z.string(), date: z.string(), rate: z.number().nullable() });
export type ExchangeRateOn = z.infer<typeof exchangeRateOnSchema>;

/**
 * `GET /localization/exchange-rates/rate` (P-24) — the rate a conversion between two currencies uses on a day, the
 * way the server resolves it: the configured provider first, else the latest rate on or before that day, else the
 * inverse pair. What an invoice converted at, asked directly.
 */
export function useExchangeRateOn(params: { from: string; to: string; date?: string | null } | null, options?: QueryOpts<ExchangeRateOn>) {
  return useApiQuery<ExchangeRateOn>(
    ['platform', 'localization', 'exchange-rates', 'rate', params?.from ?? '', params?.to ?? '', params?.date ?? ''],
    (client, signal) =>
      client.get('/api/v1/platform/localization/exchange-rates/rate', {
        query: { from: params?.from ?? '', to: params?.to ?? '', ...(params?.date ? { date: params.date } : {}) },
        schema: exchangeRateOnSchema,
        signal,
      }),
    { enabled: !!params, retry: false, ...(options ?? {}) },
  );
}

/**
 * `PUT /localization/exchange-rates` — upserts rates for a pair and a date.
 *
 * Usually a feed writes these. The human path is the correction: a rate that came in wrong, or a day a feed
 * missed, on which every conversion silently falls back to whatever the last known rate was.
 */
export function usePutExchangeRates() {
  return useApiMutation<ExchangeRate[], { rates: Array<{ base: string; quote: string; rateDate: string; rate: number; source?: string | null }> }>(
    (client, body) =>
      client.put<ExchangeRate[]>(
        '/api/v1/platform/localization/exchange-rates',
        { rates: body.rates.map((r) => ({ ...r, source: r.source ?? 'MANUAL' })) },
        { idempotencyKey: true, schema: z.array(exchangeRateSchema) },
      ),
  );
}

// ---------- Rules (RulesController, `platform/rules`) ----------

/**
 * `platform/rules`, as `RulesController` publishes it: the decision points the modules declare, and the three
 * things an administrator does against them from the console — evaluate a decision for a set of facts, validate
 * or test a rule set, and evaluate a ZEL expression. Rule sets themselves are RULESET artifacts (Studio).
 */

export const decisionPointSchema = z.object({
  key: z.string(),
  description: z.string(),
  /** A JSON Schema, as a string. */
  factSchema: z.string(),
  outcomes: z.array(z.string()),
  actions: z.array(z.object({ type: z.string(), description: z.string(), parameterSchema: z.string() })).default([]),
  defaultOutcome: z.string().nullable().optional(),
});
export type DecisionPoint = z.infer<typeof decisionPointSchema>;

/** `ContextRequest`: the scope a rule or a characteristic is evaluated in; every member optional. */
export interface RuleContext {
  warehouseId?: string | null;
  partyId?: string | null;
  processCode?: string | null;
  roleCodes?: string[] | null;
  userId?: string | null;
}

export const ruleDecisionSchema = z.object({
  decisionPoint: z.string(),
  outcome: z.string().nullable(),
  actions: z.array(z.object({ type: z.string(), parameters: z.record(z.string(), z.unknown()).default({}), ruleId: z.string() })).default([]),
  matchedRules: z.array(z.object({ ruleSetKey: z.string(), ruleSetVersion: z.number(), ruleId: z.string(), ruleName: z.string(), priority: z.number() })).default([]),
  trace: z
    .object({
      ruleSets: z.array(z.object({ key: z.string(), version: z.number(), scopeLevel: z.string(), scopeId: z.string().nullable().optional(), mode: z.string() })).default([]),
      evaluations: z.array(z.object({ ruleId: z.string(), matched: z.boolean(), failedPredicate: z.string().nullable().optional() })).default([]),
      durationMicros: z.number(),
    })
    .optional(),
});
export type RuleDecision = z.infer<typeof ruleDecisionSchema>;

export const ruleIssueSchema = z.object({ severity: z.enum(['ERROR', 'WARNING']), path: z.string(), code: z.string(), message: z.string() });
export const ruleSetValidationSchema = z.object({
  ruleSetKey: z.string(),
  valid: z.boolean(),
  errors: z.array(ruleIssueSchema).default([]),
  warnings: z.array(ruleIssueSchema).default([]),
  issues: z.array(ruleIssueSchema).default([]),
});
export type RuleSetValidation = z.infer<typeof ruleSetValidationSchema>;

export const ruleSetTestReportSchema = z.object({
  ruleSetKey: z.string(),
  total: z.number(),
  passed: z.number(),
  allPassed: z.boolean(),
  results: z.array(z.object({ name: z.string(), passed: z.boolean(), actualOutcome: z.string().nullable().optional(), actualMatchedRuleIds: z.array(z.string()).default([]), message: z.string().nullable().optional() })).default([]),
});
export type RuleSetTestReport = z.infer<typeof ruleSetTestReportSchema>;

export const zelResultSchema = z.object({ value: z.unknown(), type: z.string(), text: z.string() });
export type ZelResult = z.infer<typeof zelResultSchema>;

export const ruleSetModes = ['FIRST_MATCH', 'ALL_MATCHES', 'PRIORITY', 'COMPOSE'] as const;

/** `GET /rules/decision-points` — every decision point the modules declare (`rules:decision-point:read`). */
export function useDecisionPointRegistry(options?: QueryOpts<DecisionPoint[]>) {
  return useApiQuery<DecisionPoint[]>(
    queryKeys.custom('platform', 'rules', 'decision-points'),
    (client, signal) => client.get('/api/v1/platform/rules/decision-points', { schema: z.array(decisionPointSchema), signal }),
    { staleTime: 5 * 60_000, ...(options ?? {}) },
  );
}

/**
 * `RulesOperationsService.RuleSetBindingView` — one RULESET artifact written against a decision point (G-122).
 * `version` / `status` are the version whose content names the point: the live one when it does (`published`),
 * otherwise the one in flight. `mode` is the hit policy as written (`FIRST_MATCH` when left out).
 */
export const ruleSetBindingSchema = z.object({
  artifactId: z.string(),
  key: z.string(),
  name: z.string(),
  version: z.number(),
  status: artifactStatusSchema,
  scope: scopeRefSchema,
  mode: z.string(),
  published: z.boolean(),
  publishedVersion: z.number().nullable(),
  inFlightVersion: z.number().nullable(),
  inFlightStatus: artifactStatusSchema.nullable(),
});
export type RuleSetBinding = z.infer<typeof ruleSetBindingSchema>;
/** `GET /rules/decision-points/{key}/rule-sets` (`rules:decision-point:read` + `metadata:artifact:read`). */
export function useDecisionPointRuleSets(key: string | undefined, options?: QueryOpts<RuleSetBinding[]>) {
  return useApiQuery<RuleSetBinding[]>(
    queryKeys.custom('platform', 'rules', 'decision-points', key ?? '', 'rule-sets'),
    (client, signal) => client.get(`/api/v1/platform/rules/decision-points/${encodeURIComponent(key ?? '')}/rule-sets`, { schema: z.array(ruleSetBindingSchema), signal }),
    { enabled: !!key, ...(options ?? {}) },
  );
}

/** `POST /rules/evaluate` — what the published rule sets decide for these facts (`rules:decision:evaluate`). */
export function useEvaluateDecision() {
  return usePost<RuleDecision, { decisionPoint: string; facts: Record<string, unknown>; context?: RuleContext | null }>('/api/v1/platform/rules/evaluate', { schema: ruleDecisionSchema });
}

/** `POST /rules/validate` — a rule set document checked against its decision point (`rules:ruleset:validate`). */
export function useValidateRuleSet() {
  return usePost<RuleSetValidation, { ruleSet: Record<string, unknown>; context?: RuleContext | null }>('/api/v1/platform/rules/validate', { schema: ruleSetValidationSchema });
}

/** `POST /rules/test` — the rule set's own test cases, run (`rules:ruleset:validate`). */
export function useTestRuleSet() {
  return usePost<RuleSetTestReport, { ruleSet: Record<string, unknown>; context?: RuleContext | null }>('/api/v1/platform/rules/test', { schema: ruleSetTestReportSchema });
}

/** `POST /rules/zel/evaluate` — one expression against a scope (`rules:expression:evaluate`). */
export function useEvaluateExpression() {
  return usePost<ZelResult, { expression: string; scope?: Record<string, unknown> | null; context?: RuleContext | null }>('/api/v1/platform/rules/zel/evaluate', { schema: zelResultSchema });
}

// ---------- Characteristics register (CharacteristicController, `platform/characteristics`) ----------

/**
 * The definitions register: which characteristics exist per context type. Definitions are CHARACTERISTIC
 * artifacts, so the controller publishes no create or edit — the register reads, Studio writes.
 */
export const characteristicDataTypes = ['TEXT', 'INTEGER', 'DECIMAL', 'BOOLEAN', 'DATE', 'DATETIME', 'ENUM', 'REFERENCE'] as const;

/** `ContextType` — tolerant of the older `{code,label,module}` shape a mock may still answer with. */
export const characteristicContextTypeSchema = z.preprocess(
  (raw) => {
    const r = raw as Record<string, unknown>;
    return { contextType: r.contextType ?? r.code, label: r.label ?? r.contextType ?? r.code, description: r.description ?? r.module ?? '' };
  },
  z.object({ contextType: z.string(), label: z.string(), description: z.string() }),
);
export type CharacteristicContextType = z.infer<typeof characteristicContextTypeSchema>;

export const characteristicDefinitionSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  dataType: z.string(),
  appliesTo: z.array(z.string()).default([]),
  unit: z.string().nullable().optional(),
  referenceKind: z.string().nullable().optional(),
  allowedValues: z.array(z.string()).default([]),
  group: z.string().nullable().optional(),
  displayOrder: z.number().default(0),
  searchable: z.boolean().default(false),
  permission: z.string().nullable().optional(),
  defaultValueRule: z.string().nullable().optional(),
  visibleWhen: z.string().nullable().optional(),
  requiredWhen: z.string().nullable().optional(),
  readOnlyWhen: z.string().nullable().optional(),
  validations: z.array(z.object({ code: z.string(), min: z.number().nullable().optional(), max: z.number().nullable().optional(), minLength: z.number().nullable().optional(), maxLength: z.number().nullable().optional(), pattern: z.string().nullable().optional(), message: z.string().nullable().optional() })).default([]),
  /** W13: the name and description in other languages; `name`/`description` are the default. */
  names: z.record(z.string(), z.string()).default({}),
  descriptions: z.record(z.string(), z.string()).default({}),
});
export type CharacteristicDefinition = z.infer<typeof characteristicDefinitionSchema>;

/** `GET /characteristics/context-types` (`characteristic:definition:read`). */
export function useCharacteristicContextTypes(options?: QueryOpts<CharacteristicContextType[]>) {
  return useApiQuery<CharacteristicContextType[]>(
    queryKeys.custom('platform', 'characteristics', 'context-types'),
    (client, signal) => client.get('/api/v1/platform/characteristics/context-types', { schema: z.array(characteristicContextTypeSchema), signal }),
    { staleTime: 5 * 60_000, ...(options ?? {}) },
  );
}

/** `GET /characteristics/{contextType}/definitions` (`characteristic:definition:read`). */
export function useCharacteristicDefinitions(contextType: string | undefined, options?: QueryOpts<CharacteristicDefinition[]>) {
  return useApiQuery<CharacteristicDefinition[]>(
    queryKeys.custom('platform', 'characteristics', 'definitions', contextType ?? ''),
    (client, signal) => client.get(`/api/v1/platform/characteristics/${encodeURIComponent(contextType ?? '')}/definitions`, { schema: z.array(characteristicDefinitionSchema), signal }),
    { enabled: !!contextType, ...(options ?? {}) },
  );
}

/** `FieldError` (`one.zagru.platform.kernel.error.FieldError`): one finding of a validation, `rejectedValue` when known. */
export const platformFieldErrorSchema = z.object({ field: z.string(), code: z.string(), message: z.string(), rejectedValue: z.unknown().optional() });
export type PlatformFieldError = z.infer<typeof platformFieldErrorSchema>;
/**
 * `POST /characteristics/{contextType}/validate` (`characteristic:definition:read`) — checks candidate values
 * against the published definitions for the context without storing anything; an empty list means valid.
 */
export function useValidateCharacteristicValues() {
  return useApiMutation<PlatformFieldError[], { contextType: string; values: Record<string, unknown>; facts?: Record<string, unknown> }>((client, { contextType, ...body }) =>
    client.post(`/api/v1/platform/characteristics/${encodeURIComponent(contextType)}/validate`, body, { idempotencyKey: true, schema: z.array(platformFieldErrorSchema) }),
  );
}

// ---------- Events operations (EventsOperationsController, `platform/events`) ----------

/** `OutboxSummary`: the state of the transactional outbox. */
export const outboxSummarySchema = z.object({ pending: z.number(), failed: z.number(), publishedLast24h: z.number(), oldestPending: z.string().nullable().optional() });
export type OutboxSummary = z.infer<typeof outboxSummarySchema>;

export const replayResultSchema = z.object({ requested: z.number(), eventIds: z.array(z.string()).default([]) });
export type ReplayResult = z.infer<typeof replayResultSchema>;

/** `GET /events/outbox/summary` (`platform:events:read`). */
export function useOutboxSummary(options?: QueryOpts<OutboxSummary>) {
  return useApiQuery<OutboxSummary>(queryKeys.custom('platform', 'events', 'outbox-summary'), (client, signal) => client.get('/api/v1/platform/events/outbox/summary', { schema: outboxSummarySchema, signal }), options);
}

/** `POST /events/replay` — republishes already-published events of a window (`platform:events:replay`). */
export function useReplayEvents() {
  return usePost<ReplayResult, { eventType?: string | null; from: string; to: string; limit: number }>('/api/v1/platform/events/replay', { schema: replayResultSchema });
}

/** `POST /events/outbox/retry-failed` — resets FAILED rows to PENDING; answers with the count (`platform:events:replay`). */
export function useRetryFailedOutbox() {
  return useApiMutation<number, void>((client) => client.post<number>('/api/v1/platform/events/outbox/retry-failed', undefined, { idempotencyKey: true, schema: z.number() }));
}

/** The outbox row states (`events.outbox_event.status`). */
export const outboxStatuses = ['PENDING', 'PUBLISHED', 'FAILED'] as const;
export type OutboxStatus = (typeof outboxStatuses)[number];

/**
 * `OutboxOperations.OutboxRowView` (G-125): one row of the current tenant's outbox — the envelope's routing metadata
 * and its delivery state. The payload is deliberately not published: `platform:events:read` is an operations
 * permission, the payload is the publishing module's business data behind that module's own read permission.
 */
export const outboxRowSchema = z.object({
  eventId: z.string(),
  eventType: z.string(),
  eventVersion: z.number(),
  topic: z.string(),
  aggregateType: z.string().nullable(),
  aggregateId: z.string().nullable(),
  status: z.enum(outboxStatuses),
  attempts: z.number(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  publishedAt: z.string().nullable(),
  correlationId: z.string().nullable(),
  sourceModule: z.string().nullable(),
  actorType: z.string().nullable(),
});
export type OutboxRow = z.infer<typeof outboxRowSchema>;
export const outboxRowPageSchema = z.object({
  content: z.array(outboxRowSchema),
  page: z.number(),
  size: z.number(),
  totalElements: z.number(),
  totalPages: z.number(),
});
export type OutboxRowPage = z.infer<typeof outboxRowPageSchema>;

/** Query of `GET /events/outbox`; `eventType` ending in `*` is a prefix, `from`/`to` are ISO instants over `createdAt`. */
export interface OutboxRowParams {
  status?: OutboxStatus;
  eventType?: string;
  aggregateId?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

/** `GET /events/outbox` (`platform:events:read`): the tenant's outbox rows, newest first, 200 a page at most. */
export function useOutboxRows(params: OutboxRowParams, options?: QueryOpts<OutboxRowPage>) {
  return useApiQuery<OutboxRowPage>(
    queryKeys.custom('platform', 'events', 'outbox-rows', JSON.stringify(params)),
    (client, signal) => client.get('/api/v1/platform/events/outbox', { query: { ...params }, schema: outboxRowPageSchema, signal }),
    options,
  );
}

// ---------- Notification definitions (NotificationDefinitionController) ----------

/** `DefinitionView`: a published NOTIFICATION artifact as the sender sees it. */
export const notificationDefinitionSchema = z.object({
  key: z.string(),
  name: z.string(),
  category: z.string(),
  channels: z.array(z.enum(notificationChannels)).default([]),
  severity: z.enum(notificationSeverities),
  kind: z.enum(notificationKinds),
  version: z.number(),
  platformLevel: z.boolean(),
  /** The NOTIFICATION artifact it was published from, for opening it in Studio (G-129). */
  artifactId: z.string(),
});
export type NotificationDefinition = z.infer<typeof notificationDefinitionSchema>;

/** `GET /notifications/definitions` — the definitions a notification can be sent from. */
export function useNotificationDefinitions(options?: QueryOpts<NotificationDefinition[]>) {
  return useApiQuery<NotificationDefinition[]>(
    ['platform', 'notifications', 'definitions'],
    (client, signal) => client.get<NotificationDefinition[]>('/api/v1/platform/notifications/definitions', { schema: z.array(notificationDefinitionSchema), signal }),
    options,
  );
}

/** `GET /notifications/{id}` — one notification with its deliveries (`notification:notification:read`). */
export function useNotification(id: string | undefined, options?: QueryOpts<Notification>) {
  return useApiQuery<Notification>(
    ['platform', 'notifications', 'one', id ?? ''],
    (client, signal) => client.get<Notification>(`/api/v1/platform/notifications/${encodeURIComponent(id ?? '')}`, { schema: notificationSchema, signal }),
    { enabled: !!id, ...(options ?? {}) },
  );
}

/** `GET /document-bindings/generation-points` — the points in a document's life a binding can name. */
export function useDocumentGenerationPoints(options?: QueryOpts<string[]>) {
  return useApiQuery<string[]>(
    ['platform', 'document-bindings', 'generation-points'],
    (client, signal) => client.get<string[]>('/api/v1/platform/document-bindings/generation-points', { schema: z.array(z.string()), signal }),
    options,
  );
}

/** `PrinterType` as the server declares it: a printer takes PDF documents or ZPL labels. */
export const printerTypeCodes = ['PDF', 'ZPL'] as const;
